import React, { useEffect, useId, useRef, useState } from "react";
import { AppWindow, MessageSquare, Pause, Play, PlayCircle, Quote, TrendingUp, X } from "lucide-react";

import { AiVisibilityModalOverlay } from "../AiVisibilityModalOverlay";
import { COPY } from "./copy";
import { SidePanel } from "./SidePanel";
import "./HowAttributionWorks.scss";

const INK = "#262626";
const MUTED = "#8C8C8C";
const LINE = "#E5E7EB";
const SURFACE = "#F8FAFC";
const ACCENT = "#2F54EB";
const ACCENT_SOFT = "#F0F5FF";
const POSITIVE = "#5B9A5E";

const STEP_ICONS = [MessageSquare, Quote, AppWindow, TrendingUp];

type ReportState = "NOT_CONFIGURED" | "SYNCING" | "CONFIGURED";

// ── Flow diagram ─────────────────────────────────────────────────────────────
//
// All four steps render at once and stay put; the spotlight and the travelling
// token are what move. An earlier version crossfaded scene to scene, which meant
// you could only ever see one step and never the relationship between them.
//
// Column centres are 220 units apart, which must match the hops in the ra-token
// keyframes in HowAttributionWorks.scss.

const COL_X = [110, 330, 550, 770];
const COL_W = 180;
const RAIL_Y = 186;
const VIEW_W = 880;
const VIEW_H = 206;

function StepHeader({ index, col }: { index: number; col: number }) {
    const left = col - COL_W / 2;
    return (
        <>
            <circle cx={left + 9} cy={26} r="9" fill={ACCENT_SOFT} />
            <text x={left + 9} y={29.5} fontSize="9.5" fontWeight="700" fill={ACCENT} textAnchor="middle">
                {index + 1}
            </text>
            <text x={left + 24} y={30} fontSize="10" fontWeight="600" fill={INK}>
                {COPY.howItWorks.steps[index].label}
            </text>
        </>
    );
}

function Art1({ animated, clipId }: { animated: boolean; clipId: string }) {
    const a = COPY.howItWorks.animation;
    const col = COL_X[0];
    return (
        <>
            {/* Soft tinted bubble — fill instead of an outline, so it doesn't read as a box. */}
            <rect x={col - 78} y={80} width="156" height="42" rx="16" fill={ACCENT_SOFT} />
            <path d={`M${col - 62} 122 L${col - 70} 136 L${col - 46} 122 Z`} fill={ACCENT_SOFT} />
            <defs>
                <clipPath id={clipId}>
                    {/* Widened by CSS to reveal the question one character at a time. */}
                    <rect
                        className={animated ? "ra-type-clip" : undefined}
                        x={col - 66}
                        y={86}
                        width="132"
                        height="30"
                    />
                </clipPath>
            </defs>
            <g clipPath={`url(#${clipId})`}>
                <text x={col - 66} y={106} fontSize="10.5" fill={INK} fontWeight="500">
                    {a.question}
                </text>
            </g>
        </>
    );
}

function Art2({ animated }: { animated: boolean }) {
    const a = COPY.howItWorks.animation;
    const col = COL_X[1];
    return (
        <>
            {a.sources.map((src, i) => {
                const highlighted = src === a.brand;
                const y = 76 + i * 24;
                return (
                    <g key={src} className={animated && highlighted ? "ra-chip-in" : undefined}>
                        <rect
                            x={col - 75}
                            y={y}
                            width="150"
                            height="18"
                            rx="9"
                            fill={highlighted ? ACCENT_SOFT : SURFACE}
                            stroke={highlighted ? ACCENT : "none"}
                            strokeWidth={highlighted ? 1.4 : 0}
                        />
                        <circle cx={col - 63} cy={y + 9} r="2.6" fill={highlighted ? ACCENT : LINE} />
                        <text
                            x={col - 53}
                            y={y + 12.5}
                            fontSize="9"
                            fill={highlighted ? ACCENT : MUTED}
                            fontWeight={highlighted ? 600 : 400}>
                            {src}
                        </text>
                    </g>
                );
            })}
        </>
    );
}

function Art3({ animated }: { animated: boolean }) {
    const a = COPY.howItWorks.animation;
    const col = COL_X[2];
    return (
        <>
            {/* One outline only: a browser silhouette, no traffic lights or filler bars. */}
            <rect x={col - 72} y={74} width="144" height="72" rx="12" fill="#FFFFFF" stroke={LINE} />
            <path d={`M${col - 72} 96 H${col + 72}`} stroke={LINE} />
            <text x={col - 60} y={90} fontSize="8.5" fill={MUTED}>
                {a.landingPage}
            </text>
            <rect x={col - 58} y={108} width="86" height="5" rx="2.5" fill={LINE} />
            <circle
                className={animated ? "ra-dot" : undefined}
                cx={col + 50}
                cy={130}
                r="3.6"
                fill={POSITIVE}
            />
            <text x={col + 42} y={133} fontSize="8" fill={POSITIVE} textAnchor="end">
                {a.sessionLabel}
            </text>
        </>
    );
}

function Art4({ animated }: { animated: boolean }) {
    const a = COPY.howItWorks.animation;
    const col = COL_X[3];
    const baseline = 146;
    const bars = [
        { x: col - 63, h: 24, fill: "#DDE3F5" },
        { x: col - 37, h: 36, fill: "#DDE3F5" },
        { x: col - 11, h: 30, fill: "#DDE3F5" },
        { x: col + 15, h: 58, fill: ACCENT }
    ];
    return (
        <>
            {bars.map((b, i) => (
                <rect
                    key={b.x}
                    // Only the last bar animates — it's the one carrying the value.
                    className={animated && i === bars.length - 1 ? "ra-bar" : undefined}
                    x={b.x}
                    y={baseline - b.h}
                    width="18"
                    height={b.h}
                    rx="5"
                    fill={b.fill}
                />
            ))}
            <path d={`M${col - 70} ${baseline} H${col + 42}`} stroke={LINE} strokeLinecap="round" />
            <g className={animated ? "ra-value" : undefined}>
                <rect x={col - 58} y={70} width="116" height="21" rx="10.5" fill="#FFFFFF" stroke={ACCENT} />
                <text x={col} y={84.5} fontSize="10.5" fill={ACCENT} fontWeight="600" textAnchor="middle">
                    {a.valueLabel}
                </text>
            </g>
        </>
    );
}

/**
 * Airy by design: no panel cards, no dashed connectors, one rail. The earlier
 * version nested a card inside a card inside a card and read as a grid of boxes
 * rather than as a flow.
 */
function FlowDiagram({ animated }: { animated: boolean }) {
    const clipId = `ra-type-clip-${useId().replace(/:/g, "")}`;
    const steps = COPY.howItWorks.steps;

    return (
        <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            className="block h-auto w-full"
            role="img"
            aria-label={steps.map((s, i) => `${i + 1}. ${s.label}`).join(". ")}>
            {/* Soft spotlight on the step being explained — a tint, not a ring. */}
            {animated &&
                COL_X.map((col, i) => (
                    // The tint lives on the inner rect: the keyframes drive the group's
                    // opacity to 1, and the two multiply. Putting opacity on the animated
                    // element itself would let the CSS override it into a solid block.
                    <g key={`focus-${col}`} className={`ra-focus ra-step-${i + 1}`}>
                        <rect
                            x={col - COL_W / 2 - 8}
                            y={10}
                            width={COL_W + 16}
                            height={152}
                            rx="18"
                            fill={ACCENT}
                            opacity="0.06"
                        />
                    </g>
                ))}

            {COL_X.map((col, i) => (
                <StepHeader key={col} index={i} col={col} />
            ))}

            <Art1 animated={animated} clipId={clipId} />
            <Art2 animated={animated} />
            <Art3 animated={animated} />
            <Art4 animated={animated} />

            {/* A single rail carries the eye left to right. */}
            <path
                d={`M${COL_X[0]} ${RAIL_Y} H${COL_X[3]}`}
                stroke={LINE}
                strokeWidth="2"
                strokeLinecap="round"
            />
            {COL_X.map((col) => (
                <circle key={`stop-${col}`} cx={col} cy={RAIL_Y} r="3" fill={LINE} />
            ))}

            {animated && (
                <g className="ra-token">
                    <circle cx={COL_X[0]} cy={RAIL_Y} r="10" fill={ACCENT} opacity="0.16" />
                    <circle cx={COL_X[0]} cy={RAIL_Y} r="5" fill={ACCENT} />
                </g>
            )}
        </svg>
    );
}

// ── Step cards ───────────────────────────────────────────────────────────────

function StepFlow() {
    return (
        <ol className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {COPY.howItWorks.steps.map((step, i) => {
                const Icon = STEP_ICONS[i];
                // Step 2 is the join only CitedBy can make — give it the accent.
                const emphasised = i === 1;
                return (
                    <li
                        key={step.label}
                        className={`relative rounded-xl border p-3.5 ${
                            emphasised ? "border-geekblue-300 bg-geekblue-100/50" : "border-gray-200 bg-white"
                        }`}>
                        <div className="flex items-center gap-2">
                            <span
                                className={`flex h-6 w-6 flex-none items-center justify-center rounded-md ${
                                    emphasised ? "bg-geekblue-600 text-white" : "bg-gray-100 text-gray-500"
                                }`}>
                                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {i + 1}
                            </span>
                            {emphasised && (
                                <span className="ml-auto rounded-full bg-geekblue-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                                    {COPY.howItWorks.citedByTag}
                                </span>
                            )}
                        </div>
                        <p className="mt-2 text-[13px] font-semibold text-gray-900">{step.label}</p>
                        <p className="mt-1 text-[12px] leading-relaxed text-gray-500">{step.body}</p>
                    </li>
                );
            })}
        </ol>
    );
}

// ── Flow modal ───────────────────────────────────────────────────────────────

function useReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => setReduced(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);
    return reduced;
}

function FlowModal({
    open,
    onClose,
    reportState,
    onSetup
}: {
    open: boolean;
    onClose: () => void;
    reportState: ReportState;
    onSetup: () => void;
}) {
    const [panelOpen, setPanelOpen] = useState(false);
    const reducedMotion = useReducedMotion();
    const [userPaused, setUserPaused] = useState(false);
    const [tabActive, setTabActive] = useState(true);
    const [visible, setVisible] = useState(true);
    const hostRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const onVisibility = () => setTabActive(!document.hidden);
        onVisibility();
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    // No CPU burn if the diagram is scrolled out of the modal's viewport.
    useEffect(() => {
        const node = hostRef.current;
        if (!open || !node || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver((entries) => setVisible(entries[0]?.isIntersecting ?? true), {
            threshold: 0.15
        });
        io.observe(node);
        return () => io.disconnect();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const animated = !reducedMotion;
    const paused = userPaused || !tabActive || !visible;

    return (
        <AiVisibilityModalOverlay onBackdropClick={onClose} zClassName="z-[1100]">
            <div className="w-full max-w-[980px] overflow-hidden rounded-2xl bg-white shadow-2xl">
                <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-[17px] font-semibold text-gray-900">{COPY.howItWorks.title}</h2>
                        <p className="mt-0.5 text-[12.5px] text-gray-500">{COPY.howItWorks.modalSubtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="-mr-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                        <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                </header>

                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                    <div
                        ref={hostRef}
                        className={`ra-anim relative overflow-x-auto rounded-xl border border-gray-200 bg-white p-3${
                            paused ? " is-paused" : ""
                        }`}>
                        <div className="min-w-[620px]">
                            <FlowDiagram animated={animated} />
                        </div>
                        {animated && (
                            <button
                                type="button"
                                onClick={() => setUserPaused((v) => !v)}
                                aria-label={
                                    userPaused ? COPY.howItWorks.animation.play : COPY.howItWorks.animation.pause
                                }
                                className="absolute bottom-2 right-2 rounded-md border border-gray-200 bg-white/90 p-1 text-gray-400 transition-colors hover:text-gray-600">
                                {userPaused ? (
                                    <Play className="h-3 w-3" strokeWidth={2} />
                                ) : (
                                    <Pause className="h-3 w-3" strokeWidth={2} />
                                )}
                            </button>
                        )}
                    </div>

                    <div className="mt-4 space-y-4">
                        <StepFlow />
                        {reportState === "NOT_CONFIGURED" && (
                            <Requirements
                                onSetup={() => {
                                    onClose();
                                    onSetup();
                                }}
                            />
                        )}
                        <HonestyNote onOpenPanel={() => setPanelOpen(true)} />
                    </div>
                </div>
            </div>

            {/* Raised above this modal's own overlay. */}
            <SmallNumberPanel open={panelOpen} onClose={() => setPanelOpen(false)} zClassName="z-[1300]" />
        </AiVisibilityModalOverlay>
    );
}

// ── Requirements (NOT_CONFIGURED only) ───────────────────────────────────────

function Requirements({ onSetup }: { onSetup: () => void }) {
    const r = COPY.howItWorks.requirements;
    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
            <ol className="space-y-2">
                {r.items.map((item, i) => (
                    <li key={item.label} className="flex items-center gap-2.5">
                        <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white text-[10.5px] font-semibold text-gray-500 ring-1 ring-gray-200">
                            {i + 1}
                        </span>
                        <span className="flex-1 text-[12.5px] text-gray-700">{item.label}</span>
                        <span className="text-[11.5px] tabular-nums text-gray-400">{item.time}</span>
                    </li>
                ))}
            </ol>
            <p className="mt-3 text-[12.5px] font-medium text-gray-900">{r.noCode}</p>
            <button
                type="button"
                onClick={onSetup}
                className="mt-3.5 rounded-lg bg-geekblue-600 px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-geekblue-700">
                {COPY.empty.primaryCta}
            </button>
        </div>
    );
}

// ── Honesty note ─────────────────────────────────────────────────────────────

function HonestyNote({ onOpenPanel }: { onOpenPanel: () => void }) {
    const h = COPY.howItWorks.honesty;
    return (
        <div className="rounded-xl border border-geekblue-200 bg-geekblue-100/40 px-4 py-3.5">
            <p className="text-[13px] font-semibold text-gray-900">{h.title}</p>
            {h.paragraphs.map((p) => (
                <p key={p} className="mt-1.5 text-[12.5px] leading-relaxed text-gray-700">
                    {p}
                </p>
            ))}
            <button
                type="button"
                onClick={onOpenPanel}
                className="mt-2.5 text-[12.5px] font-medium text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline">
                {h.panelLink}
            </button>
        </div>
    );
}

function SmallNumberPanel({
    open,
    onClose,
    zClassName
}: {
    open: boolean;
    onClose: () => void;
    zClassName?: string;
}) {
    const p = COPY.howItWorks.smallNumberPanel;
    return (
        <SidePanel open={open} title={p.title} onClose={onClose} zClassName={zClassName}>
            <div className="space-y-5">
                {p.sections.map((s) => (
                    <div key={s.title}>
                        <h3 className="text-[13px] font-semibold text-gray-900">{s.title}</h3>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-gray-600">{s.body}</p>
                    </div>
                ))}
                <p className="rounded-lg border-l-2 border-geekblue-400 bg-geekblue-100/50 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-gray-700">
                    {p.closing}
                </p>
            </div>
        </SidePanel>
    );
}

// ── Shell ────────────────────────────────────────────────────────────────────

/**
 * The explainer is a single link. Everything — the flow diagram, the four steps,
 * the setup requirements and the honesty note — lives in the modal it opens, so
 * the tab leads with the report rather than with a wall of explanation.
 */
export function HowAttributionWorks({ reportState, onSetup }: { reportState: ReportState; onSetup: () => void }) {
    const [flowOpen, setFlowOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setFlowOpen(true)}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline">
                <PlayCircle className="h-3.5 w-3.5" strokeWidth={2} />
                {COPY.howItWorks.title}
            </button>

            <FlowModal
                open={flowOpen}
                onClose={() => setFlowOpen(false)}
                reportState={reportState}
                onSetup={onSetup}
            />
        </>
    );
}

export default HowAttributionWorks;
